/**
 * Service for managing encryption keys in localStorage.
 * Similar to AdminKeyService but stores encryption parameters for client-side encryption.
 */
class EncryptionKeyService {
    constructor() {
        this.ENCRYPTION_KEYS_KEY = 'encryptionKeys';
    }

    /**
     * Get encryption parameters for a specific election.
     * @param {string} electionId The election ID
     * @returns {Object|null} Encryption parameters or null if not found
     */
    getEncryptionKey(electionId) {
        const keys = this.getEncryptionKeys();
        return keys[electionId] || null;
    }

    /**
     * Store encryption parameters for a specific election.
     * @param {string} electionId The election ID
     * @param {Object} encryptionParams The encryption parameters
     * @param {number} encryptionParams.scheme The encryption scheme number
     * @param {string} encryptionParams.key Base64url encoded key
     */
    setEncryptionKey(electionId, encryptionParams) {
        const keys = this.getEncryptionKeys();
        keys[electionId] = encryptionParams;
        localStorage.setItem(this.ENCRYPTION_KEYS_KEY, JSON.stringify(keys));
    }

    /**
     * Get all stored encryption keys.
     * @returns {Object} Map of election IDs to encryption parameters
     */
    getEncryptionKeys() {
        const stored = localStorage.getItem(this.ENCRYPTION_KEYS_KEY);
        return stored ? JSON.parse(stored) : {};
    }

    /**
     * Remove encryption parameters for a specific election.
     * @param {string} electionId The election ID
     */
    removeEncryptionKey(electionId) {
        const keys = this.getEncryptionKeys();
        delete keys[electionId];
        localStorage.setItem(this.ENCRYPTION_KEYS_KEY, JSON.stringify(keys));
    }

    /**
     * Check if encryption parameters exist for a specific election.
     * @param {string} electionId The election ID
     * @returns {boolean} True if encryption parameters exist
     */
    hasEncryptionKey(electionId) {
        return this.getEncryptionKey(electionId) !== null;
    }

    /**
     * Clear all encryption keys.
     */
    clearAllKeys() {
        localStorage.removeItem(this.ENCRYPTION_KEYS_KEY);
    }
}

const encryptionKeyService = new EncryptionKeyService();
export default encryptionKeyService;
